import classNames from 'classnames';
import type { Client, PickerResponse } from 'filestack-js';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { ToastType } from 'react-toastify';

import type { DisplayCreative, IDimensionDefinition, TAdGroup } from '@feathr/blackbox';
import {
  CreativeClass,
  ValidDimensions,
  ValidFacebookDimensions,
  ValidFacebookVideoDimensions,
} from '@feathr/blackbox';
import type { ISelectOption } from '@feathr/components';
import {
  Button,
  Checkbox,
  CodeMirror,
  CopyToClipboardButton,
  EmptyState,
  Fieldset,
  Input,
  Popover,
  Radios,
  Select,
  toast,
  Toolbar,
  Well,
} from '@feathr/components';
import { Flaggable } from '@feathr/extender/components';
import { SessionCacheProvider } from '@feathr/extender/hooks';
import { useStore } from '@feathr/extender/state';

import { useResetLookalikeCreativeAdGroup } from './CreativeMetadata.hooks';

import * as styles from './CreativeMetadata.css';

interface IProps {
  creative: DisplayCreative;
  destinationUrlPlaceholder?: string;
  showInlineEditor?: boolean;
  showReplaceButton?: boolean;
  showName?: boolean;
  showStatus?: boolean;
  validDimensions?: Record<string, IDimensionDefinition>;
}

// TODO: This is duplicated - find a better way to make creative stuff reusable

/**
 * Get the dimensions of a video
 * @param url - The url of the video
 * @returns The dimensions of the video
 */
export async function getVideoDimensions(url: string): Promise<{ width: number; height: number }> {
  return new Promise<{ width: number; height: number }>((resolve) => {
    const video = document.createElement('video');
    const handleLoadedMetadata = (): void => {
      const dimensions = {
        width: video.videoWidth,
        height: video.videoHeight,
      };
      // Clean up
      video.removeEventListener('loadedmetadata', handleLoadedMetadata);
      video.src = '';
      // Forces the media element to reset
      video.load();
      resolve(dimensions);
    };
    video.addEventListener('loadedmetadata', handleLoadedMetadata);
    video.src = url;
  });
}

function CreativeMetadata({
  creative,
  destinationUrlPlaceholder,
  showInlineEditor,
  showReplaceButton,
  showStatus = false,
  validDimensions,
}: Readonly<IProps>): JSX.Element {
  const { Campaigns } = useStore();
  const { t } = useTranslation();
  const type = creative.get('_cls');
  const isVideo = type === CreativeClass.DisplayVideo || type === CreativeClass.FacebookVideo;
  const campaignId = creative.get('parent');
  const campaign = campaignId ? Campaigns.get(campaignId) : undefined;

  const isOmniWithoutReach = campaign?.isOmniCampaign && !campaign?.isOmniReachCampaign;
  const hasActiveLookalikeTargeting = useResetLookalikeCreativeAdGroup({ creative });

  let dimensions = validDimensions ?? {
    ...ValidDimensions,
  };

  if (type === CreativeClass.FacebookImage) {
    dimensions = { ...ValidFacebookDimensions };
  } else if (type === CreativeClass.FacebookVideo) {
    dimensions = { ...ValidFacebookVideoDimensions };
  }

  const dimensionOptions = Object.keys(dimensions).map<ISelectOption>((key: string) => {
    const value = dimensions[key];
    return {
      id: value.spec,
      name: value.name,
    };
  });
  const dimension = dimensionOptions.find((dim) => dim.id === creative.get('spec'));

  function handleDestinationChange(newValue?: string): void {
    creative.set({
      use_original_destination: newValue === 'use_original_destination',
    });
  }

  function handleEnabledChange(newValue?: string): void {
    creative.set({ is_enabled: newValue === 'enabled' });
  }

  // TODO: The creative edit page should allow only letting the user upload images that match the current spec.
  function setImage(client: Client): (files: PickerResponse) => void {
    return (files: PickerResponse) => {
      const { filesUploaded } = files;
      filesUploaded.forEach(async (file) => {
        const { key, container, handle } = file;
        const url = `https://s3.amazonaws.com/${container}/${encodeURIComponent(key!)}`;
        const metadata = await client.metadata(handle, {
          width: true,
          height: true,
          mimetype: true,
          size: true,
          filename: true,
        });

        let { width, height } = metadata;
        if (isVideo) {
          const videoDimensions = await getVideoDimensions(url);
          width = videoDimensions.width;
          height = videoDimensions.height;
        }

        const { mimetype, filename } = metadata;
        creative.set({ height, width, mimetype, url, name: `${filename} (${width}x${height})` });

        const validDimensions =
          campaign && !campaign?.isPending
            ? creative.getValidDimensions(campaign)
            : creative.getValidDimensionsByCreativeClass(type);

        // Check if the uploaded dimensions match any valid spec
        const isValidDimension = Object.values(validDimensions).some(
          (dim) => dim.width === width && dim.height === height,
        );

        if (!isValidDimension) {
          const validSpecs = Object.values(validDimensions)
            .map((dim) => `${dim.width}x${dim.height}`)
            .join(', ');

          toast(
            t(
              '"{{filename}}" has invalid dimensions ({{width}}x{{height}}). Valid dimensions are: {{validSpecs}}',
              { filename, width, height, validSpecs },
            ),
            { type: ToastType.ERROR },
          );
          creative.reload();
          return;
        }

        // Find the matching spec for the valid dimensions
        const matchingSpec = Object.values(validDimensions).find(
          (dim) => dim.width === width && dim.height === height,
        )?.spec;

        if (matchingSpec) {
          creative.set({ spec: matchingSpec });
        }

        const specKeys = Object.values(dimensions).map(
          (spec: IDimensionDefinition): string => spec.spec,
        );
        if (!specKeys.includes(creative.get('spec'))) {
          const closest = creative.getClosestValidAspectRatio(dimensions);

          toast(
            t(
              '"{{filename}}" is the wrong aspect ratio {{aspectRatio}} ({{width}}x{{height}}). The closest valid aspect ratio would be {{closest}}.',
              { filename, width, height, closest, aspectRatio: creative.aspectRatio },
            ),
            { type: 'error' },
          );
          creative.reload();
        }
      });
    };
  }

  async function handleReplace(): Promise<void> {
    const filestackJs = await import(/* webpackChunkName: "filestack-js" */ 'filestack-js');
    const client = filestackJs.init(FILESTACK_API_KEY);

    // TODO: add `video/quicktime` back when we implement proper mov support
    const metaVideo = ['video/mp4'];
    const adVideo = ['video/mp4', 'video/webm'];

    const videoTypes = type === CreativeClass.FacebookVideo ? metaVideo : adVideo;

    client
      .picker({
        maxFiles: 1,
        maxSize: 200 * 1024 * 1024,
        accept: isVideo ? videoTypes : ['image/jpeg', 'image/png', 'image/gif'],
        onUploadDone: setImage(client),
        transformations: {
          crop: false,
          circle: false,
          rotate: false,
        },
        dropPane: {},
      })
      .open();
  }

  function getDimensionsOptionLabel({ id }: ISelectOption): string {
    const key = Object.keys(dimensions).find((k) => dimensions[k].spec === id);
    const d = key ? dimensions[key] : { height: 0, name: t('Unknown Dimensions'), width: 0 };
    return `${d.width}px x ${d.height}px (${d.name})`;
  }

  function getDimensionsOptionValue({ id }: ISelectOption): string {
    const key = Object.keys(dimensions).find((k) => dimensions[k].spec === id);
    const d = key ? dimensions[key] : { height: 0, name: t('Unknown Dimensions'), width: 0 };
    return `${d.width}px x ${d.height}px (${d.name})`;
  }

  function handleSelectDimensions({ id }: ISelectOption): void {
    const key = Object.keys(dimensions).find((k) => dimensions[k].spec === id);
    if (key) {
      creative.set({
        spec: dimensions[key].spec,
        width: dimensions[key].width,
        height: dimensions[key].height,
      });
    }
  }

  const uploaded = creative.get('url');

  const button = (
    <Button
      className={styles.upload}
      onClick={handleReplace}
      type={uploaded ? 'primary' : 'secondary'}
    >
      {!uploaded ? t('Upload') : isVideo ? t('Replace video') : t('Replace image')}
    </Button>
  );

  const adGroups = creative.get('ad_groups');
  const hasLookalikeAudiences = adGroups?.includes('lookalike') ?? false;
  const hasTargetedGroups = adGroups?.includes('main') ?? false;

  const isEmptyAdGroup = adGroups === null || adGroups === undefined;
  const hasAllAudiences = isEmptyAdGroup
    ? false
    : adGroups.length === 0 || (hasLookalikeAudiences && hasTargetedGroups);

  const allAudiencesValue: boolean | 'indeterminate' =
    !hasAllAudiences && adGroups !== null && adGroups !== undefined && adGroups.length > 0
      ? 'indeterminate'
      : hasAllAudiences;

  function toggleAllAudiences(newValue?: boolean): void {
    creative.set({ ad_groups: newValue ? [] : null });
  }

  function toggleAdGroup(groupType: TAdGroup, newValue?: boolean): void {
    // Handle null case (none selected)
    if (adGroups === null || adGroups === undefined) {
      if (newValue) {
        creative.set({ ad_groups: [groupType] });
      }
      return;
    }

    const currentGroups = adGroups;
    const otherGroup = groupType === 'main' ? 'lookalike' : 'main';

    // If current state is [] (all audiences), we need to handle it specially
    if (currentGroups.length === 0) {
      if (newValue) {
        // Already all, do nothing
        return;
      } else {
        // Unchecking from all means only the other group remains
        creative.set({ ad_groups: [otherGroup] });
        return;
      }
    }

    let newGroups: TAdGroup[];

    if (newValue) {
      // Add group if not already present
      if (currentGroups.includes(groupType)) {
        return;
      }
      newGroups = [...currentGroups, groupType];
    } else {
      // Remove group
      newGroups = currentGroups.filter((group) => group !== groupType);
    }

    // If both are selected, set to [] (all audiences)
    if (newGroups.includes('main') && newGroups.includes('lookalike')) {
      creative.set({ ad_groups: [] });
    } else if (newGroups.length === 0) {
      // If neither is selected, set to null (none)
      creative.set({ ad_groups: null });
    } else {
      creative.set({ ad_groups: newGroups });
    }
  }

  function toggleTargetedGroups(newValue?: boolean): void {
    toggleAdGroup('main', newValue);
  }

  function toggleLookalikeAudiences(newValue?: boolean): void {
    toggleAdGroup('lookalike', newValue);
  }

  return (
    <>
      {showReplaceButton &&
        type !== CreativeClass.DisplayAdTag &&
        type !== CreativeClass.DisplayBannersnack &&
        (uploaded ? (
          button
        ) : (
          <EmptyState label={t('No image or video uploaded')} theme={'neutral'}>
            {button}
          </EmptyState>
        ))}
      {showInlineEditor && type === CreativeClass.DisplayAdTag && (
        <>
          <CodeMirror
            attribute={'adtag'}
            helpText={t(
              'Copy and paste your ad tag markup here. Add Feathr macros to enable impression and click tracking.',
            )}
            label={t('Ad tag HTML')}
            model={creative}
            name={'adtag'}
            options={{ height: 136 }}
          />
          <Toolbar>
            <Popover toggle={'onMouseOver'}>
              <span>
                <CopyToClipboardButton t={t} text={'@FEATHR_TRACK@'}>
                  <code>@FEATHR_TRACK@</code>
                </CopyToClipboardButton>
              </span>
              <div>
                <Trans t={t}>
                  This macro will be replaced with an HTML <code>img</code> tag for tracking
                  impressions.
                </Trans>
              </div>
            </Popover>
            <Popover toggle={'onMouseOver'}>
              <span>
                <CopyToClipboardButton t={t} text={'@FEATHR_CLICK@'}>
                  <code>@FEATHR_CLICK@</code>
                </CopyToClipboardButton>
              </span>
              <div>
                {t('This macro will be replaced with a click-through URL for tracking clicks.')}
              </div>
            </Popover>
            <Popover toggle={'onMouseOver'}>
              <span>
                <CopyToClipboardButton t={t} text={'@FEATHR_CLICK_ESC@'}>
                  <code>@FEATHR_CLICK_ESC@</code>
                </CopyToClipboardButton>
              </span>
              <div>
                {t(
                  'This macro will be replaced with a URI-encoded version of the click-through URL for tracking clicks.',
                )}
              </div>
            </Popover>
          </Toolbar>
          <Fieldset>
            <Select
              getOptionLabel={getDimensionsOptionLabel}
              getOptionValue={getDimensionsOptionValue}
              label={t('Dimensions')}
              name={'dimensions'}
              onSelectSingle={handleSelectDimensions}
              options={dimensionOptions}
              value={dimension}
            />
          </Fieldset>
        </>
      )}
      <Input attribute={'name'} label={t('Name')} model={creative} type={'text'} />
      <Input
        attribute={'alt_text'}
        helpText={t(
          'Provide advertisement text with a clear call-to-action for cases where the image cannot load and for screen readers used by visually-impaired individuals. (This text will be overridden by any alternate text provided on the creative.)',
        )}
        label={t('Alternate text')}
        model={creative}
        optional={true}
        placeholder={t(
          "Advertisement: Don't bee too late to register for Beecon 2023! Click here to register now!",
        )}
        type={'text'}
      />
      {type === CreativeClass.DisplayAdTag && (
        <Radios
          helpText={t(
            'Sometimes ad tags come with built-in destination URLs and click areas. You can keep these or override them with the destination URL configured here.',
          )}
          label={t('Override built-in destination URLs')}
          onChange={handleDestinationChange}
          options={[
            {
              id: 'override_original_destination',
              name: t('Override built-in destination URLs'),
            },
            { id: 'use_original_destination', name: t('Use built-in destination URLs') },
          ]}
          value={
            creative.get('use_original_destination')
              ? 'use_original_destination'
              : 'override_original_destination'
          }
        />
      )}
      <Input
        attribute={'destination_url'}
        disabled={creative.get('use_original_destination')}
        label={t('Destination URL')}
        model={creative}
        optional={true}
        placeholder={destinationUrlPlaceholder}
        type={'url'}
      />
      {/* Only display ad groups checkboxes if there's a lookalike targeting */}
      {isOmniWithoutReach && hasActiveLookalikeTargeting && (
        <SessionCacheProvider>
          <Flaggable featureName={'showLookalikeAudienceTargeting'}>
            <Well
              className={classNames(styles.adGroupsWell, {
                [styles.adGroupsWellError]: isEmptyAdGroup,
              })}
              layout={'vertical'}
              theme={isEmptyAdGroup ? 'red' : undefined}
            >
              <Checkbox
                className={styles.allAudiencesCheckbox}
                label={t('All available audiences')}
                onChange={toggleAllAudiences}
                value={allAudiencesValue}
              />
              <Checkbox
                label={t('Targeted groups')}
                onChange={toggleTargetedGroups}
                value={hasTargetedGroups || hasAllAudiences}
              />
              <Checkbox
                label={t('Lookalike audiences')}
                onChange={toggleLookalikeAudiences}
                validationError={
                  isEmptyAdGroup ? t('Please select at least one audience') : undefined
                }
                value={hasLookalikeAudiences || hasAllAudiences}
              />
            </Well>
          </Flaggable>
        </SessionCacheProvider>
      )}

      {showStatus && (
        <Radios
          label={t('Creative status')}
          layout={'block'}
          onChange={handleEnabledChange}
          options={[
            { id: 'enabled', name: t('Enabled') },
            { id: 'disabled', name: t('Disabled') },
          ]}
          value={creative.get('is_enabled') ? 'enabled' : 'disabled'}
        />
      )}
    </>
  );
}

export default observer(CreativeMetadata);
